import React from 'react';
import { Phone, Mail, MapPin, MessageCircle, Instagram, Facebook } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_tokefrio-launch/artifacts/82hhhvkm_image.png";
const WHATSAPP_NUMBER = "5565992258000";
const WHATSAPP_MESSAGE = "Olá! Gostaria de mais informações sobre os serviços da Tokefrio.";
const INSTAGRAM_URL = "https://www.instagram.com/tokefrio/";
const FACEBOOK_URL = "https://www.facebook.com/TokeFrio/?locale=pt_BR";

export const Footer = () => {
  const handleWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, '_blank');
  };

  const currentYear = new Date().getFullYear();

  return (
    <footer
      id="contato"
      data-testid="footer-section"
      className="footer-gradient py-16 lg:py-20"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12 mb-12">
          {/* Logo & Description */}
          <div className="lg:col-span-2" data-testid="footer-about">
            <img
              src={LOGO_URL}
              alt="Tokefrio Climatização"
              className="h-14 object-contain mb-6"
              data-testid="footer-logo"
            />
            <p className="font-manrope text-sm text-slate-400 leading-relaxed max-w-md mb-6">
              A Tokefrio Climatização atua há mais de 10 anos oferecendo soluções 
              completas em climatização comercial e industrial para empresas 
              em Cuiabá e região.
            </p>
            
            {/* Social Media */}
            <div className="flex items-center gap-3" data-testid="footer-social">
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 hover:bg-sky-600 rounded-lg flex items-center justify-center transition-all duration-300"
                data-testid="social-instagram"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5 text-white" />
              </a>
              <a
                href={FACEBOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-slate-800 hover:bg-sky-600 rounded-lg flex items-center justify-center transition-all duration-300"
                data-testid="social-facebook"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5 text-white" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div data-testid="footer-links">
            <h3 className="font-outfit text-lg font-semibold text-white mb-5">
              Links Rápidos
            </h3>
            <ul className="space-y-3">
              <li>
                <a
                  href="#sobre"
                  className="font-manrope text-sm text-slate-400 hover:text-sky-400 transition-colors"
                  data-testid="footer-link-sobre"
                >
                  Sobre a Empresa
                </a>
              </li>
              <li>
                <a
                  href="#servicos"
                  className="font-manrope text-sm text-slate-400 hover:text-sky-400 transition-colors"
                  data-testid="footer-link-servicos"
                >
                  Nossos Serviços
                </a>
              </li>
              <li>
                <a
                  href="#portfolio"
                  className="font-manrope text-sm text-slate-400 hover:text-sky-400 transition-colors"
                  data-testid="footer-link-portfolio"
                >
                  Áreas de Atuação
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div data-testid="footer-contact">
            <h3 className="font-outfit text-lg font-semibold text-white mb-5">
              Contato
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Phone className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="font-manrope text-sm text-slate-400">Telefone</p>
                  <a
                    href="tel:+556599225800"
                    className="font-manrope text-sm text-white hover:text-sky-400 transition-colors"
                    data-testid="footer-phone"
                  >
                    (65) 99225-8000
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Mail className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="font-manrope text-sm text-slate-400">E-mail</p>
                  <a
                    href="mailto:tokefrio@hotmail.com"
                    className="font-manrope text-sm text-white hover:text-sky-400 transition-colors"
                    data-testid="footer-email"
                  >
                    tokefrio@hotmail.com
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <div className="flex-shrink-0 w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-sky-400" />
                </div>
                <div>
                  <p className="font-manrope text-sm text-slate-400">Localização</p>
                  <span
                    className="font-manrope text-sm text-white"
                    data-testid="footer-location"
                  >
                    Cuiabá - MT
                  </span>
                </div>
              </li>
            </ul>

            {/* WhatsApp Button */}
            <button
              onClick={handleWhatsApp}
              className="mt-6 inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-all"
              data-testid="footer-whatsapp-button"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-800 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p
              className="font-manrope text-sm text-slate-500"
              data-testid="footer-copyright"
            >
              © {currentYear} Tokefrio Climatização. Todos os direitos reservados.
            </p>
            <p className="font-manrope text-xs text-slate-600">
              Climatização Comercial e Industrial • Cuiabá - MT
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
